import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import styles from "./Table.module.css";

// importing icons
import TableBody from "./TableBody";
import TableHead from "./TableHead";
import { useTableManagerContext } from "../table-manager/TableManagerContext";
import SkeletonTable from "./SkeletionTable";

const DEFAULT_IS_RECORD_SELECTABLE = () => true;

const Table = ({
  records: directRecords,
  tooltipConfig = {
    enabled: true,
    hideOnClick: false,
  },
  columns: directColumns,
  borders,
  cellPadding,
  tableHeaderProps,
  tableDataProps,
  contrastRow = true,
  setSelectedRows,
  clearSelectedRowsRef,
  columnResizeNeeded = true,
  total = -1,
  alreadySelectedIds = null,
  recordsToDisable = [],
  isRecordSelectable = DEFAULT_IS_RECORD_SELECTABLE,
  skeletonRowCount = 8,
  expandable = false,
  renderExpandedRow,
}) => {
  const {
    records: ctxRecords = directRecords,
    columns: ctxColumns = directColumns,
    columnsToDisplay,
    selectOptions: ctxSelectOptions = {},
  } = useTableManagerContext();

  const visibleColumns =
    Array.isArray(columnsToDisplay) && columnsToDisplay.length > 0
      ? columnsToDisplay
      : Array.isArray(ctxColumns)
        ? ctxColumns
        : [];

  const records = Array.isArray(ctxRecords) ? ctxRecords : directRecords || [];

  const [customColumnWidthsNeeded, setCustomColumnWidthsNeeded] =
    useState(false);

  const selectOptions = ctxSelectOptions;
  const { selectable, setSelectedRecords } = selectOptions;
  const checkRecordSelectable = useCallback(
    (record) => Boolean(isRecordSelectable(record)) !== false,
    [isRecordSelectable],
  );
  const selectableRecords = useMemo(
    () => records.filter(checkRecordSelectable),
    [records, checkRecordSelectable],
  );

  const [selected, setSelected] = useState({
    all: false,
    ...records.map(() => false),
    count: 0,
  });

  const alreadySelectedRecordStrings = alreadySelectedIds
    ? alreadySelectedIds.selected.map((item) => String(item))
    : [];
  const [alreadySelectedRecords, setAlreadySelectedRecords] = useState(
    alreadySelectedRecordStrings,
  );

  useImperativeHandle(clearSelectedRowsRef, () => ({
    clearSelected: () => {
      setSelectedRecords([]);
      setSelected({
        all: false,
        ...records.map(() => false),
        count: 0,
      });
    },
  }));

  useEffect(() => {
    if (
      alreadySelectedIds?.editable === undefined ||
      alreadySelectedIds?.editable === false
    )
      return;
    records.forEach((record) => {
      const jsonString = JSON.stringify(record);
      const recordId = alreadySelectedIds
        ? record[alreadySelectedIds?.id]
        : null;
      const isAlreadySelected = alreadySelectedRecords.includes(
        String(recordId),
      );
      if (isAlreadySelected) {
        setSelected((prev) => {
          const { count, ...prevSelected } = prev;
          const newCount = count + 1;
          const newSelected = {
            ...prevSelected,
            all: newCount === records.length,
            [jsonString]: true,
            count: newCount,
          };
          return newSelected;
        });
      }
    });
  }, [records]);

  useEffect(() => {
    if (setSelectedRecords) {
      if (selectableRecords.length) {
        const allSelected = selectableRecords.every((record) => {
          const jsonString = JSON.stringify(record);
          return selected[jsonString];
        });
        if (selected.all !== allSelected) {
          setSelected((prev) => ({ ...prev, all: allSelected }));
        }
      } else if (selected.all) {
        setSelected((prev) => ({ ...prev, all: false }));
      }

      const selectedJSONStrings = Object.entries(selected)
        .filter(([key, value]) => {
          try {
            JSON.parse(key); // check if valid JSON
            return value === true;
          } catch {
            return false;
          }
        })
        .map(([key]) => key); // extract only JSON-string keys
      const uniqueJSONStrings = [...new Set(selectedJSONStrings)];
      const selectedObjects = uniqueJSONStrings
        .map((str) => JSON.parse(str))
        .filter(checkRecordSelectable);
      setSelectedRecords(selectedObjects);
      if (setSelectedRows) setSelectedRows(selectedObjects);
    }
  }, [
    records,
    selectableRecords,
    selected,
    setSelectedRecords,
    checkRecordSelectable,
  ]);

  const tableRef = useRef(null);

  const handleSelectedChange = (e) => {
    const { id, checked } = e.target;
    if (id === "all") {
      if (!selectableRecords.length) return;
      if (alreadySelectedIds && !checked) {
        const recordIds = selectableRecords.map((record) =>
          String(record[alreadySelectedIds.id]),
        );

        setAlreadySelectedRecords((prev) =>
          prev.filter((id) => !recordIds.includes(id)),
        );
      }
      setSelected((prev) => ({
        ...prev,
        all: checked,
        ...selectableRecords.reduce((acc, record) => {
          const jsonString = JSON.stringify(record);
          const recordId = alreadySelectedIds
            ? record[alreadySelectedIds?.id]
            : null;
          const prevent =
            !alreadySelectedIds?.editable &&
            alreadySelectedRecords.some((id) => id.includes(String(recordId)));
          if (!prevent) acc[jsonString] = checked;
          return acc;
        }, {}),
        count: checked ? selectableRecords.length : 0,
      }));
    } else {
      setSelected((prev) => {
        const { count, ...prevSelected } = prev;
        const newCount = count + (checked ? 1 : -1);
        if (!checked)
          setAlreadySelectedRecords((prev) =>
            prev.filter((item) => !id.includes(item)),
          );
        const newSelected = {
          ...prevSelected,
          all: checked && newCount === records.length,
          [id]: checked,
          count: newCount,
        };
        return newSelected;
      });
    }
  };

  // render <colgroup> so only numeric-width columns get explicit widths.
  const renderColGroup = () => {
    const cols = [];

    // if selectable checkbox column exists, maintain fixed width (same as CSS: 3.57rem)
    if (selectable) {
      cols.push(<col key="select-col" style={{ width: "3.57rem" }} />);
    }

    visibleColumns.forEach((colDef, idx) => {
      cols.push(
        <col
          key={`col-${colDef.accessor || idx}`}
          style={{
            ...(colDef && typeof colDef.width === "number"
              ? { width: `${colDef.width}rem` }
              : {}),
            ...(colDef && typeof colDef.minWidth === "number"
              ? { minWidth: `${colDef.minWidth}rem` }
              : {}),
            ...(colDef && typeof colDef.maxWidth === "number"
              ? { maxWidth: `${colDef.maxWidth}rem` }
              : {}),
          }}
        />,
      );
    });

    return <colgroup>{cols}</colgroup>;
  };

  return (
    <table
      ref={tableRef}
      className={[
        styles.table,
        borders?.verticalBorders && styles.verticalBorders,
        // Yogesh : adding tableLayout : fixed only when customColumnWidthsNeeded is true (line 122)
        customColumnWidthsNeeded ? styles.fixedLayout : "",
      ].join(" ")}
      style={{
        "--border-width": borders?.borderWidth || "1px",
        "--border-color":
          total === null || total === 0
            ? "transparent"
            : borders?.borderColor || "#7070702d",
        "--border-radius": borders?.borderRadius || "3px",
        "--cell-padding": cellPadding || "1rem",
        "--select-color": selectOptions?.color || "#2F7EE9",
      }}
    >
      {/* {renderColGroup()} */}

      <TableHead
        {...{
          columns: ctxColumns,
          records,
          selected,
          handleSelectedChange,
          tableHeaderProps,
          alreadySelectedRecords,
          alreadySelectedIds,
          isRecordSelectable: checkRecordSelectable,
        }}
        columnResizeNeeded={columnResizeNeeded}
        //Yogesh : Passing setter to set the state from TableHead component (Line 136)
        setCustomColumnWidthsNeeded={setCustomColumnWidthsNeeded}
      />
      {total !== null && total !== 0 && (
        <TableBody
          {...{
            records,
            tooltipConfig,
            columns: ctxColumns,
            contrastRow,
            selectable: selectable,
            selected,
            handleSelectedChange,
            tableDataProps,
            alreadySelectedRecords,
            alreadySelectedIds,
            recordsToDisable,
            isRecordSelectable: checkRecordSelectable,
            expandable,
            renderExpandedRow,
          }}
        />
      )}
      {total === null && (
        <tbody>
          <tr>
            <td
              colSpan={ctxColumns.length + 1}
              style={{
                padding: 0,
                border: "none",
                height: "100%",
              }}
            >
              <SkeletonTable rows={skeletonRowCount} />
            </td>
          </tr>
        </tbody>
      )}
      {total !== null && (total === 0 || records.length === 0) && (
        <tbody>
          <tr>
            <td colSpan={ctxColumns.length} style={{ padding: "2rem" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <p>No records found</p>
              </div>
            </td>
          </tr>
        </tbody>
      )}
    </table>
  );
};

export default Table;